import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {formatSlNumber,parseSlNumber} from '../utils/csvUtils';

const {FiUser,FiEuro,FiCalendar,FiRotateCcw,FiPlus,FiX,FiPackage,FiFileText,FiClock}=FiIcons;

const InvoiceForm=({onAddInvoice})=> {
  const [formData,setFormData]=useState({
    stranka: '',
    amount: '',
    interval: 'monthly',
    nextDue: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
    lineItems: []
  });
  const [errors,setErrors]=useState({});

  // Calculate total amount from line items and base amount
  const calculateTotalAmount=()=> {
    const baseAmount=parseSlNumber(formData.amount) || 0;
    const lineItemsTotal=formData.lineItems.reduce((sum,item)=> sum + (parseSlNumber(item.price) || 0),0);
    return baseAmount + lineItemsTotal;
  };

  const handleChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({...prev,[name]: value}));
    if (errors[name]) {
      setErrors(prev=> ({...prev,[name]: ''}));
    }
  };

  const handleAmountChange=(e)=> {
    let value=e.target.value;
    // Allow comma as decimal separator
    setFormData(prev=> ({...prev,amount: value}));
    if (errors.amount) {
      setErrors(prev=> ({...prev,amount: ''}));
    }
  };

  const setTodayDate=()=> {
    const today=new Date();
    const year=today.getFullYear();
    const month=String(today.getMonth() + 1).padStart(2,'0');
    const day=String(today.getDate()).padStart(2,'0');
    const formattedDate=`${year}-${month}-${day}`;
    setFormData(prev=> ({...prev,nextDue: formattedDate}));
  };

  const setFirstOfNextMonth=()=> {
    const today=new Date();
    const year=today.getFullYear();
    const month=today.getMonth() + 1;
    let nextYear=year;
    let nextMonth=month + 1;

    // Handle year rollover
    if (nextMonth > 12) {
      nextMonth=1;
      nextYear=year + 1;
    }

    const formattedDate=`${nextYear}-${String(nextMonth).padStart(2,'0')}-01`;
    console.log('Setting next month date:',formattedDate); // Debug log
    setFormData(prev=> ({...prev,nextDue: formattedDate}));
  };

  const addLineItem=()=> {
    if (formData.lineItems.length < 5) {
      setFormData(prev=> ({
        ...prev,
        lineItems: [...prev.lineItems,{id: Date.now(),service: '',price: ''}]
      }));
    }
  };

  const updateLineItem=(id,field,value)=> {
    setFormData(prev=> ({
      ...prev,
      lineItems: prev.lineItems.map(item=> 
        item.id===id ? {...item,[field]: value} : item
      )
    }));
  };

  const removeLineItem=(id)=> {
    setFormData(prev=> ({
      ...prev,
      lineItems: prev.lineItems.filter(item=> item.id !==id)
    }));
  };

  const validateForm=()=> {
    const newErrors={};

    if (!formData.stranka.trim()) {
      newErrors.stranka='Ime stranke je obvezno';
    }

    const totalAmount=calculateTotalAmount();
    if (totalAmount <=0) {
      newErrors.amount='Potreben je veljaven osnovni znesek ali postavke';
    }

    if (!formData.nextDue) {
      newErrors.nextDue='Datum naslednjega plačila je obvezen';
    }

    // Validate line items
    formData.lineItems.forEach((item,index)=> {
      if (item.service.trim() && (!item.price || parseSlNumber(item.price) <=0)) {
        newErrors[`lineItem_${index}`]='Cena je obvezna za storitev';
      }
      if (item.price && parseSlNumber(item.price) > 0 && !item.service.trim()) {
        newErrors[`lineItem_${index}`]='Opis storitve je obvezen';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length===0;
  };

  const handleSubmit=(e)=> {
    e.preventDefault();
    if (validateForm()) {
      const validLineItems=formData.lineItems.filter(item=> 
        item.service.trim() && item.price && parseSlNumber(item.price) > 0
      );

      onAddInvoice({
        ...formData,
        amount: parseSlNumber(formData.amount) || 0,
        lineItems: validLineItems,
        totalAmount: calculateTotalAmount()
      });

      setFormData({
        stranka: '',
        amount: '',
        interval: 'monthly',
        nextDue: new Date().toISOString().split('T')[0],
        description: '',
        notes: '',
        lineItems: []
      });
    }
  };

  const intervals=[
    {value: 'monthly',label: 'Mesečno'},
    {value: 'quarterly',label: 'Četrtletno'},
    {value: 'half-yearly',label: 'Polletno'},
    {value: 'yearly',label: 'Letno'}
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-xl shadow-lg p-8 relative">
        {/* Header with submit button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#4d504a] flex items-center">
            <SafeIcon icon={FiPlus} className="mr-3 text-[#6e725c]" />
            Dodaj nov ponavljajoči se račun
          </h2>
          <motion.button
            type="submit"
            form="invoice-form"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#6e725c] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#5d6250] transition-colors shadow-lg flex items-center"
          >
            <SafeIcon icon={FiPlus} className="mr-2" />
            Dodaj račun
          </motion.button>
        </div>

        <form id="invoice-form" onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiUser} className="inline mr-2" />
              Stranka *
            </label>
            <input
              type="text"
              name="stranka"
              value={formData.stranka}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all ${
                errors.stranka ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Vnesite ime stranke"
            />
            {errors.stranka && (
              <p className="text-red-500 text-sm mt-1">{errors.stranka}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#4d504a] mb-2">
                <SafeIcon icon={FiEuro} className="inline mr-2" />
                Osnovni znesek
              </label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleAmountChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all ${
                  errors.amount ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0,00"
              />
              {errors.amount && (
                <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
              )}
              <p className="text-xs text-[#6e725c] mt-1">Uporabite vejico (,) za decimalno ločilo</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#4d504a] mb-2">
                <SafeIcon icon={FiRotateCcw} className="inline mr-2" />
                Interval ponavljanja
              </label>
              <select
                name="interval"
                value={formData.interval}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all"
              >
                {intervals.map(interval=> (
                  <option key={interval.value} value={interval.value}>
                    {interval.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Line Items Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-[#4d504a]">
                <SafeIcon icon={FiPackage} className="inline mr-2" />
                Dodatne storitve/postavke
              </label>
              {formData.lineItems.length < 5 && (
                <motion.button
                  type="button"
                  onClick={addLineItem}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#80b238] text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-[#6d9530] transition-colors flex items-center"
                >
                  <SafeIcon icon={FiPlus} className="mr-1" />
                  Dodaj postavko
                </motion.button>
              )}
            </div>

            {formData.lineItems.map((item,index)=> (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="md:col-span-7">
                  <input
                    type="text"
                    placeholder="Opis storitve"
                    value={item.service}
                    onChange={(e)=> updateLineItem(item.id,'service',e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all ${
                      errors[`lineItem_${index}`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="md:col-span-4">
                  <input
                    type="text"
                    placeholder="Cena (z vejico)"
                    value={item.price}
                    onChange={(e)=> updateLineItem(item.id,'price',e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all ${
                      errors[`lineItem_${index}`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="md:col-span-1 flex justify-center">
                  <button
                    type="button"
                    onClick={()=> removeLineItem(item.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-lg" />
                  </button>
                </div>
                {errors[`lineItem_${index}`] && (
                  <div className="md:col-span-12">
                    <p className="text-red-500 text-sm">{errors[`lineItem_${index}`]}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Total Amount Display */}
          {(formData.amount || formData.lineItems.length > 0) && (
            <div className="bg-blue-50 p-4 rounded-lg border border-[#6e725c]/20">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span className="text-[#4d504a]">Skupni znesek:</span>
                <span className="text-[#6e725c]">{formatSlNumber(calculateTotalAmount())} €</span>
              </div>
              {formData.lineItems.length > 0 && (
                <div className="text-sm text-[#6e725c] mt-2">
                  Osnova: {formatSlNumber(parseSlNumber(formData.amount) || 0)} € + 
                  Postavke: {formatSlNumber(formData.lineItems.reduce((sum,item)=> sum + (parseSlNumber(item.price) || 0),0))} €
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiCalendar} className="inline mr-2" />
              Naslednje plačilo *
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                name="nextDue"
                value={formData.nextDue}
                onChange={handleChange}
                className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all ${
                  errors.nextDue ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <motion.button
                type="button"
                onClick={setTodayDate}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-2 bg-[#6e725c] text-white rounded-lg text-sm font-medium hover:bg-[#5d6250] transition-colors flex items-center whitespace-nowrap"
                title="Nastavi na danes"
              >
                <SafeIcon icon={FiClock} className="mr-1 text-xs" />
                Danes
              </motion.button>
              <motion.button
                type="button"
                onClick={setFirstOfNextMonth}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-2 bg-[#3297c7] text-white rounded-lg text-sm font-medium hover:bg-[#2a7ca8] transition-colors flex items-center whitespace-nowrap"
                title="Nastavi na 1. dan naslednjega meseca"
              >
                <SafeIcon icon={FiClock} className="mr-1 text-xs" />
                1. nasl. mesec
              </motion.button>
            </div>
            {errors.nextDue && (
              <p className="text-red-500 text-sm mt-1">{errors.nextDue}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiFileText} className="inline mr-2" />
              Opis
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all resize-none"
              placeholder="Opis za prikaz na kartici (neobvezno)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiFileText} className="inline mr-2" />
              Opombe
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all resize-none"
              placeholder="Interne opombe (vidne samo v detajlih računa)"
            />
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default InvoiceForm;